import { ArrowRight, ExternalLink, Linkedin, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '@/features/settings';
import { NewsletterBand } from '@/features/newsletter';
import { ROUTES } from '@/shared/config/routes';
import { SITE } from '@/shared/config/site';
import { Seo, aboutPageSchema, breadcrumbSchema, clampDescription } from '@/shared/seo';

const FALLBACK_ABOUT =
  'Oversight Insights explains audit and investigations in plain language — real cases, no jargon, no assumed knowledge.';

function lines(value: string | undefined): string[] {
  return (value ?? '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function paragraphs(value: string | undefined): string[] {
  return (value ?? '')
    .split(/\r?\n\s*\r?\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

export function AboutPage() {
  const { data: settings } = useSiteSettings();
  const siteName = SITE.name;
  const aboutText = settings?.aboutText?.trim() || FALLBACK_ABOUT;
  const tagline = settings?.tagline?.trim() || SITE.tagline;
  const ownerName = settings?.ownerName?.trim() ?? '';
  const ownerRole = settings?.ownerRole?.trim() ?? '';
  const ownerLocation = settings?.ownerLocation?.trim() ?? '';
  const ownerImage = settings?.ownerImage?.trim() ?? '';
  const ownerLinkedInUrl = settings?.ownerLinkedInUrl?.trim() ?? '';
  const ownerFocus = lines(settings?.ownerFocus);
  const ownerBio = paragraphs(settings?.ownerBio);

  return (
    <>
      {/*
        Unlike the home page, /about has real prose of its own — so the meta
        description is drawn from the page's own copy, which is what a snippet
        is supposed to summarise.
      */}
      <Seo
        title="About"
        description={clampDescription(aboutText)}
        path={ROUTES.about}
        jsonLd={[
          aboutPageSchema(clampDescription(aboutText)),
          breadcrumbSchema([
            { name: 'Home', path: ROUTES.home },
            { name: 'About', path: ROUTES.about },
          ]),
        ]}
      />

      <section className="relative overflow-hidden bg-zinc-950">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 80% at 100% 50%, rgba(251,191,36,0.06) 0%, transparent 60%)',
          }}
        />
        <div className="relative z-10 mx-auto max-w-5xl px-6 py-20">
          <p className="mb-4 font-mono text-[10px] font-medium uppercase tracking-widest text-amber-400">
            About
          </p>
          <h1 className="font-display text-5xl font-black italic leading-none tracking-tight text-white sm:text-7xl">
            {siteName}
          </h1>
          <p className="mt-4 max-w-lg text-lg leading-relaxed text-zinc-400">{tagline}</p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
        <div className="mx-auto max-w-2xl">
          <p className="whitespace-pre-line text-[1.0625rem] leading-[1.85] text-foreground">
            {aboutText}
          </p>
        </div>

        {ownerName ? (
          <section
            aria-labelledby="owner-heading"
            className="mt-16 overflow-hidden rounded-3xl border border-border bg-card shadow-sm"
          >
            <div className="grid lg:grid-cols-[0.88fr_1.12fr]">
            <div className="relative flex min-h-[24rem] items-center justify-center overflow-hidden bg-zinc-950 p-8 sm:p-12">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    'radial-gradient(circle at 50% 45%, rgba(251,191,36,0.14) 0%, transparent 58%)',
                }}
              />
              <div
                aria-hidden="true"
                className="absolute -left-16 top-12 h-40 w-40 rounded-full border border-amber-400/20"
              />
              <div
                aria-hidden="true"
                className="absolute -bottom-20 -right-12 h-56 w-56 rounded-full border border-white/10"
              />
              {ownerImage ? (
                <img
                  src={ownerImage}
                  alt={`${ownerName}, owner of ${siteName}`}
                  width="400"
                  height="400"
                  loading="lazy"
                  decoding="async"
                  className="relative z-10 aspect-square w-full max-w-[21rem] rounded-full border border-white/15 object-cover shadow-2xl shadow-black/40"
                />
              ) : (
                <div
                  role="img"
                  aria-label={`${ownerName}, owner of ${siteName}`}
                  className="relative z-10 flex aspect-square w-full max-w-[21rem] items-center justify-center rounded-full border border-white/15 bg-zinc-900 font-display text-8xl font-black italic text-amber-400 shadow-2xl shadow-black/40"
                >
                  {ownerName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-12">
              <p className="font-mono text-[10px] font-medium uppercase tracking-widest text-amber-600 dark:text-amber-400">
                Meet the owner
              </p>
              <h2
                id="owner-heading"
                className="mt-3 font-display text-4xl font-black italic tracking-tight sm:text-5xl"
              >
                {ownerName}
              </h2>
              {ownerRole ? (
                <p className="mt-3 text-base font-semibold text-foreground">{ownerRole}</p>
              ) : null}
              {ownerLocation ? (
                <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin aria-hidden="true" className="h-4 w-4" />
                  {ownerLocation}
                </p>
              ) : null}

              {ownerFocus.length > 0 ? (
                <div className="mt-6 flex flex-wrap gap-2">
                  {ownerFocus.map((focus) => (
                    <span
                      key={focus}
                      className="rounded-full border border-border bg-muted px-3 py-1.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground"
                    >
                      {focus}
                    </span>
                  ))}
                </div>
              ) : null}

              {ownerBio.length > 0 ? (
                <div className="mt-7 space-y-4 text-[0.98rem] leading-7 text-muted-foreground">
                  {ownerBio.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              ) : null}

              {ownerLinkedInUrl ? (
                <a
                  href={ownerLinkedInUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-[#0a66c2] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#004182] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0a66c2] focus-visible:ring-offset-2"
                  aria-label={`View ${ownerName} on LinkedIn`}
                >
                  <Linkedin aria-hidden="true" className="h-4 w-4" />
                  View LinkedIn profile
                  <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
                </a>
              ) : null}
              </div>
            </div>
          </section>
        ) : null}

        <div className="mx-auto mt-16 max-w-2xl">
          <NewsletterBand
            title="One audit lesson a week."
            subtitle={`Join the readers of ${siteName}.`}
          />

          <div className="mt-12 text-center">
            <Link
              to={ROUTES.home}
              className="mx-auto inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowRight className="h-3 w-3 rotate-180" />
              Back to articles
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
